import { z } from 'zod';
/**
 * Feature-flag names — the closed set of switches the product has.
 *
 * Naming: `<area>.<capability>`. A flag lives here BEFORE any code reads it, so a check
 * against a misspelled name is a compile error rather than a permanently-off feature.
 * Each entry declares its DEFAULT — what a request sees when the flag store is unreachable
 * or has no row — and that default is the safe direction: a new capability defaults OFF,
 * a kill switch for existing behaviour defaults ON. A shipped capability flips to ON, so
 * every organisation has it without an admin visiting Feature flags; the flag stays as an
 * off switch (candidates, customers, jobs, custom fields: on since 2026-10-15).
 *
 * Retiring a flag is a two-step change: remove every read, then remove the name here.
 * The type system finds the reads.
 */
export const FLAGS = {
    'candidates.enabled': { default: true, description: 'Candidate records and pipeline views.' },
    'customers.enabled': { default: true, description: 'Customer (client) records.' },
    'messages.enabled': {
        default: true,
        description: "Message Hub's texts and emails on projects and contacts (TS-1891): the Messages API and tab, and Pending messages on Home and Team Tasks. The feed that fills them (TS-1892) runs whatever this says.",
    },
    'messages.replies': {
        default: false,
        description: 'Reply to texts from the Messages tab through Twilio (TS-1904). Also needs messages.enabled and the Twilio secret.',
    },
    'home.agenda': {
        default: true,
        description: "The Calendar on Home and Team Tasks: Calendly meetings (a day or a month), read from the Data team's BigQuery by the worker (calendly-meetings).",
    },
    'jobs.enabled': {
        default: true,
        description: 'Jobs under deals: the recruiting side of a project (RELAY-950).',
    },
    'custom_fields.enabled': { default: true, description: 'Per-entity custom field definitions.' },
    'pipelines.rollup_rules': {
        default: false,
        description: 'Stage roll-up rules: a parent moves stage when any/all of its children reach one (RELAY-948).',
    },
    'pipelines.triggers': {
        default: false,
        description: 'Pipeline triggers: create a task or note, or call a URL, when a record enters, leaves or sits in a stage.',
    },
    'audit_log.enabled': {
        default: true,
        description: 'Kill switch for audit-log writes. ON unless it is actively breaking writes.',
    },
    'events.outbox_publish': {
        default: true,
        description: 'Kill switch for draining the event outbox to subscribers.',
    },
    'api_keys.enabled': {
        default: false,
        description: 'API keys for integrations (TS-954): Settings → API keys, and key authentication. Off rejects every key.',
    },
    'webhooks.enabled': {
        default: false,
        description: 'Outbound webhooks (TS-958): Settings → Webhooks, and delivery of events to subscribed URLs. Off stops new deliveries.',
    },
    'recruitcrm.sync': {
        default: false,
        description: 'CRM → RecruitCRM sync (TS-1835): queue changed candidates, companies, contacts, jobs and notes and write them to RecruitCRM. Off: nothing is queued and queued jobs complete without calling RecruitCRM. Global (every organisation).',
    },
    'recruitcrm.sync_dry_run': {
        default: true,
        description: 'RecruitCRM sync dry run (TS-1835): build each payload and log its field names and hash, but call RecruitCRM for nothing and write nothing back. Only matters while recruitcrm.sync is on.',
    },
    'recruitcrm.receive': {
        default: false,
        description: 'RecruitCRM → CRM receiver (TS-1858): RecruitCRM webhooks write the values of linked custom fields (field_identities) into the CRM. Off: webhooks are answered and ignored. Global (every organisation).',
    },
    'signup.auto_activate': {
        default: true,
        description: 'TEMPORARY, for testing: a first sign-in from the organisation’s email domain is an ACTIVE Employee (create and edit records). Off: an INVITED Viewer an admin must activate. Turn off before real users arrive.',
    },
    'filters.dsl_builder': {
        default: false,
        description: 'On the candidates, companies, projects, deals and jobs lists, Add filter opens the condition-by-condition filter builder in place of the field side panel, and the bar shows no quick-filter chips. No toggle: the flag alone decides. Off: quick filters and the Add filter side panel.',
    },
    'logging.debug': {
        default: false,
        description: 'Raise API/worker log level to debug at runtime, without a deploy (RELAY-932).',
    },
};
export const FLAG_NAMES = Object.keys(FLAGS);
export const flagNameSchema = z.enum(FLAG_NAMES);
export const flagStateSchema = z.object({
    name: flagNameSchema,
    enabled: z.boolean(),
    /** Free-text — who/why; shown in the admin UI, never evaluated. */
    note: z.string().max(500).optional(),
});
/** Resolve a flag from a partial set of overrides, falling back to its declared default. */
export function resolveFlag(name, overrides) {
    return overrides[name] ?? FLAGS[name].default;
}
/**
 * Evaluation context (RELAY-936): what a flag can be targeted on. The API builds it from the
 * authenticated request; the web app gets its flags from the API, never from flagd directly.
 */
export const flagContextSchema = z.object({
    /** OpenFeature targeting key — the user id. */
    targetingKey: z.string().min(1),
    organisationId: z.string().min(1),
    organisationSlug: z.string().min(1),
    plan: z.string().min(1),
    userId: z.string().min(1),
    environment: z.enum(['development', 'test', 'staging', 'production']),
});
/** Organisation plans a flag may target. `plan` lives on the organisation row. */
export const PLANS = ['trial', 'standard', 'enterprise'];
export const planSchema = z.enum(PLANS);
//# sourceMappingURL=flags.js.map